import axios, { AxiosError, AxiosResponse } from 'axios'
import { toast } from 'react-toastify'
import i18n from 'i18next'
import { store } from '../stores/store'
import FavAuctions from './Favorites'
import ActivityPanel from './activity'
import Auctions from './auction'
import Contact from './contact'
import CurrencyExchange from './currency'
import HomeFiltering from './home'
import Logs from './logs'
import Packages from './packages'
import Payments from './payments'
import Posts from './post'
import Profile from './profile'
import Account from './account'
import Bid from './bids'
import { ServerError } from '../models/serverError'

interface CustomHttpResponse {
  status: number
  errors: string | { [key: string]: string }[]
}
const handleErrorResponse = (data: any) => {
  if (data) {
    const errorMessage = i18n.t(data)
    toast.error(errorMessage)
  }
}

const sleep = (delay: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, delay)
  })

axios.defaults.baseURL = process.env.REACT_APP_API_URL

axios.interceptors.request.use((config) => {
  const { token } = store.commonStore
  if (token) config.headers!.Authorization = `Bearer ${token}`
  return config
})

axios.interceptors.response.use(
  async (response) => {
    if (process.env.NODE_ENV === 'development') await sleep(300)
    return response
  },
  (error: AxiosError) => {
    const errorData = error.response?.data as CustomHttpResponse
    if (error.message === 'Network Error') {
      toast.error('Database is updating.')
    } else {
      // const { data, status, headers } = error.response
      switch (error.response?.status) {
        case 400:
          if (typeof errorData.errors === 'string') {
            handleErrorResponse(error.response.data.errors)
          } else {
            for (const key in errorData.errors) {
              if (errorData.errors[key]) {
                handleErrorResponse(errorData.errors[key])
              }
            }
          }
          break
        case 401:
          if (
            errorData.status === 401 &&
            error.response.headers['www-authenticate']?.startsWith('Bearer error="invalid_token"')
          ) {
            store.userStore.logout()
            toast.error('Session expired - please login again')
          }
          break
        case 404:
          // history.push("/not-found");
          toast.error('Resource not found')
          break
        case 500:
          store.commonStore.setServerError(error.response.data as ServerError)
          toast.error('Server Error')
          break
        case 503:
          toast.error('Database is not available')
          break
      }
    }
    return Promise.reject(error)
  },
)

const responseBody = <T>(response: AxiosResponse<T>) => response.data

export const requests = {
  get: <T>(url: string) => axios.get<T>(url).then(responseBody),
  post: <T>(url: string, body: any, config?: any) =>
    axios.post<T>(url, body, config).then(responseBody),
  put: <T>(url: string, body: any, config?: any) =>
    axios.put<T>(url, body, config).then(responseBody),
  del: <T>(url: string) => axios.delete<T>(url).then(responseBody),
}

const agent = {
  Account,
  Profile,
  Contact,
  Auctions,
  Posts,
  Payments,
  ActivityPanel,
  FavAuctions,
  Packages,
  Logs,
  HomeFiltering,
  CurrencyExchange,
  requests,
  Bid,
}

export default agent
